/**
 * 36_CUSTOM — la phrase qu'un refus du SERVEUR porte, pour une interface qui parle sa langue.
 *
 * Décision PO (dérogation) : un refus MÉTIER qu'aucun écran ne traduit se dit, dans l'interface
 * française, par la phrase précise que le serveur rédige en français ; dans toute autre langue, par
 * la phrase générique traduite de l'écran. ⛔ Jamais une phrase française dans l'interface anglaise,
 * ni le code brut dans aucune.
 *
 * ⭐ Une seule écriture de la règle : le dernier rang de la dérogation (`phraseDuRefus`, `components/custom-fields/phraseDuRefusDeDerogation.ts`)
 * et les fenêtres qui rendent une phrase pour TOUT refus (« Nouvel intervenant », « Ajouter un
 * intervenant ») l'appellent — une copie divergerait au premier changement de langue du serveur.
 * Le toast commun (`toastApiError`) et l'enregistrement automatique (`refusDuServeur`) disent un
 * 422 par `phraseDeSaisiePourLInterface` : la même règle, appliquée à chaque erreur de saisie.
 * ⚠️ Où vit la phrase : `message` pour une `ApiError` ordinaire ; une mutation qui garde le CODE pour
 * `message` (`useCreateInstructor`, `useCreateInstructorMember`) range le corps du refus dans
 * `details`, dont `message` porte la phrase. Lire `message` là-bas montrait le code.
 * ⚠️ Un lecteur qui ne trouve aucune phrase écrit un repli TECHNIQUE (`REPLI_TECHNIQUE`) : ce
 * n'est pas une phrase du serveur, l'écran dit alors la sienne.
 * ⚠️ Le serveur rédige en français, SAUF des introuvables restés en anglais (« Email template not
 * found ») : pour l'interface française, ils cèdent la place à la phrase générique traduite
 * (`INTROUVABLE_EN_ANGLAIS`) — jamais une phrase anglaise sur l'écran français.
 */
import type { TFunction } from 'i18next'

import {
    codeDuRefusTransverse,
    direLesEntrees,
    phraseDEntreeDeValidation,
    statutDuRefus,
    type EntreeDeValidation,
} from './api-error-transverse'

/** La langue dans laquelle le serveur rédige ses refus — en français, dans le code. */
export const LANGUE_DU_SERVEUR = 'fr'

/**
 * Le repli d'un lecteur qui n'a trouvé aucune phrase : un statut, jamais une phrase — « API error
 * 404 » (`handleApiError`), « API error: 404 » (`handleQuoteError`, `erreurRedigee`), « HTTP 404 »
 * (`handleTenderError`, l'enregistrement automatique), « Erreur 404 » (`useEntityDocuments`), et le
 * libellé technique anglais d'une porte du coffre suivi du statut, « Failed to create folder: 429 »
 * (`erreurLue`, `lib/api/ged-owned-by-tender-erreurs.ts`).
 */
const REPLI_TECHNIQUE = /^(?:(?:API error:?|HTTP|Erreur) \d{3}|[A-Za-z][A-Za-z ]*: \d{3})$/

/**
 * Un introuvable que le serveur écrit encore en ANGLAIS. Mesuré le 2026-10-02 sur `backend/src` :
 * 22 phrases distinctes, 75 émetteurs (« Email template not found », « Group <id> not found »,
 * « Profile not found »…), toutes de la famille `*_NOT_FOUND`, toutes « … not found ».
 */
const INTROUVABLE_EN_ANGLAIS = /\bnot found\b/i

/** Le préfixe que Pydantic pose devant la phrase d'un validateur (`raise ValueError(…)`). */
const PREFIXE_DU_VALIDATEUR = /^Value error,\s*/i

/** Ce que Pydantic écrit LUI-MÊME, en anglais, sous le type `value_error` — `EmailStr` : « value is
 *  not a valid email address: … ». Ce n'est pas la phrase d'un validateur du module. */
const ANGLAIS_DE_PYDANTIC = /^value is not a valid\b/i

/** Un code levé tel quel par un validateur (`raise ValueError("TAG_NOT_FOUND")`). */
const CODE_BRUT = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/

/** La langue de l'interface, telle qu'`i18next` l'a résolue (`useTranslation().i18n`). */
export function langueDeLInterface(i18n: { resolvedLanguage?: string; language?: string } | undefined): string | undefined {
    return i18n?.resolvedLanguage ?? i18n?.language
}

/** La phrase que le serveur a RÉDIGÉE pour ce refus, où qu'elle soit rangée ; `''` sans phrase. */
export function phraseRedigeeParLeServeur(err: unknown): string {
    if (!(err instanceof Error)) return ''
    const message = REPLI_TECHNIQUE.test(err.message) ? '' : err.message
    if (message && message !== codeDuRefusTransverse(err)) return message
    const details = (err as { details?: unknown }).details
    const phrase = details && typeof details === 'object' ? (details as { message?: unknown }).message : undefined
    return typeof phrase === 'string' ? phrase : ''
}

/**
 * Pour un REFUS (4xx) seulement : la phrase du serveur quand l'interface parle sa langue — et que
 * le serveur l'a écrite dans cette langue —, `''` sinon : l'appelant dit alors sa phrase générique,
 * traduite. Une panne (5xx) n'y passe jamais : elle se dit par sa référence (`toastApiError`).
 */
export function phraseDuServeurPourLInterface(err: unknown, langue: string | undefined): string {
    const statut = statutDuRefus(err)
    if (statut === null || statut >= 500 || !langue?.startsWith(LANGUE_DU_SERVEUR)) return ''
    const phrase = phraseRedigeeParLeServeur(err)
    return INTROUVABLE_EN_ANGLAIS.test(phrase) ? '' : phrase
}

/** La phrase qu'un VALIDATEUR du serveur a rédigée (`value_error`, sans « Value error, »), `''`
 *  sinon : ni l'anglais de Pydantic, ni un code brut. */
function phraseDuValidateur(entree: EntreeDeValidation): string {
    if (entree.type !== 'value_error' || typeof entree.msg !== 'string') return ''
    const phrase = entree.msg.replace(PREFIXE_DU_VALIDATEUR, '').trim()
    return ANGLAIS_DE_PYDANTIC.test(phrase) || CODE_BRUT.test(phrase) ? '' : phrase
}

/**
 * Les erreurs d'un 422, dites pour l'interface : la phrase qu'un validateur a RÉDIGÉE quand
 * l'interface parle la langue du serveur ; sinon, et pour tout autre type, la phrase de la table
 * (son type et sa borne). Toutes, sans doublon.
 */
export function phraseDeSaisiePourLInterface(
    entrees: EntreeDeValidation[],
    t: TFunction,
    langue: string | undefined,
): string {
    const redigee = langue?.startsWith(LANGUE_DU_SERVEUR) ? phraseDuValidateur : () => ''
    return direLesEntrees(entrees, (entree) => redigee(entree) || phraseDEntreeDeValidation(entree, t))
}
